function hasWatermarkPosition(position = {}) {
    return Object.values(position).some(Boolean);
}

function getWatermarkPosition(position = {}) {
    return Object.keys(position).find(key => position[key]) || '';
}

export function getBatchSizeBucket(count) {
    if (count <= 1) {
        return '1';
    }

    if (count <= 5) {
        return '2-5';
    }

    if (count <= 20) {
        return '6-20';
    }

    return '20+';
}

export function summarizeUploadedImages(images = []) {
    const formats = [...new Set(images.map(image => image.type?.split('/')[1]).filter(Boolean))];
    const totalBytes = images.reduce((sum, image) => sum + (image.fileSize?.bytes || 0), 0);

    return {
        count: images.length,
        formats: formats.join(','),
        total_mb: totalBytes > 0 ? Math.round((totalBytes / (1024 * 1024)) * 10) / 10 : 0,
    };
}

export function getAppliedFeatures(options, { cropped = [] } = {}) {
    const features = [];

    if (options.background?.remove) {
        features.push('background_remove');
    }

    if (options.background?.applyToBackground) {
        features.push('background_effects');
    }

    if (options.background?.behindSubject && options.background?.layers?.length > 0) {
        features.push('behind_subject');
    }

    if (options.watermark?.text) {
        features.push('watermark_text');
    }

    if (options.watermark?.images?.length > 0) {
        features.push('watermark_image');
    }

    if (options.resizeW !== 'auto' || options.resizeH !== 'auto') {
        features.push('resize');
    }

    if (options.rotate) {
        features.push('rotate');
    }

    if (options.flipH || options.flipV) {
        features.push('flip');
    }

    if (options.zoom) {
        features.push('zoom');
    }

    if (options.cropToRectangle) {
        features.push('crop_to_rectangle');
    }

    if (cropped.length > 0) {
        features.push('crop');
    }

    if (options.contrast || options.brightness || options.hue || options.saturation
        || options.vibrance || options.sepia) {
        features.push('colors');
    }

    if (options.invertRed || options.invertGreen || options.invertBlue) {
        features.push('invert_channels');
    }

    if (options.blur || options.denoise || options.unsharpMask?.radius || options.unsharpMask?.strength) {
        features.push('blur_sharp');
    }

    if (options.noise || options.ink || options.pixelate || options.vignette?.size
        || options.vignette?.amount || options.lensBlur?.radius) {
        features.push('decor');
    }

    if (options.effects?.enabled) {
        features.push(`effects_${options.effects.style || 'tiles'}`);
    }

    if (options.normalize) {
        features.push('normalize');
    }

    return features;
}

export function getDownloadAnalytics(options, { cropped = [], imageCount = 0 } = {}) {
    const features = getAppliedFeatures(options, { cropped });
    const watermark = options.watermark || {};

    return {
        features: features.join(','),
        feature_count: features.length,
        batch_size: getBatchSizeBucket(imageCount),
        output_format: options.saveAs || 'original',
        watermark_text: Boolean(watermark.text),
        watermark_image: (watermark.images || []).length > 0,
        watermark_position: getWatermarkPosition(watermark.position),
        watermark_overlay: Boolean(watermark.imagesPosition?.overlay),
        behind_layer_count: options.background?.layers?.length || 0,
    };
}

export { hasWatermarkPosition, getWatermarkPosition };
